import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Save, X } from 'lucide-react'
import { getAccounts, getControlAccounts, getDefaultExtras, getSetupStandard, saveControlAccounts, saveDefaultExtras } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { ACCOUNT_ROLES, MasterTabs, TRANSACTIONS, USED_BY, isHeading } from './masterShared'
import { DefaultsGuide } from './MasterGuides'

// Extra accounts work as one of these, and take accounts of that type.
const KINDS = [
  { id: 'Cash', label: 'Cash', type: 'Asset' },
  { id: 'Card', label: 'Bank and card', type: 'Asset' },
  { id: 'AR', label: 'Accounts receivable', type: 'Asset' },
  { id: 'AP', label: 'Accounts payable', type: 'Liability' },
]
const KIND = Object.fromEntries(KINDS.map((k) => [k.id, k]))
// An account a role can take: its type; null (paid from) an asset or
// liability; undefined (an unknown role) any account.
const fits = (a, type) => (type === undefined || (type ? a.type === type : a.type === 'Asset' || a.type === 'Liability'))
let rowKey = 0

// Transaction accounts: for each kind of transaction (a sale at the till, an
// invoice, a vendor bill...), the accounts it posts to. Accounts several
// transactions share are marked; extra accounts to choose from, and each
// store's own till accounts, follow.
export default function DefaultAccounts() {
  const qc = useQueryClient()
  const { notify } = useToast()
  const { data: accounts = [], isLoading: loadingAccounts } = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['control-accounts'], queryFn: getControlAccounts })
  const saved = useMemo(() => Object.fromEntries(rows.map((r) => [r.name, String(r.account_code || '').trim()])), [rows])
  const [mapping, setMapping] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => { setMapping(saved) }, [saved])
  // Extra accounts to choose from, and each store's own till accounts.
  const { data: extras } = useQuery({ queryKey: ['default-extras'], queryFn: getDefaultExtras })
  const strip = (rows) => rows.map(({ default_id, name, kind, account_code }) => ({ default_id: default_id || null, name: name.trim(), kind, account_code }))
  const savedExtras = useMemo(() => JSON.stringify({ custom: strip(extras?.custom || []), locations: extras?.locations || {} }), [extras])
  const [custom, setCustom] = useState([])
  const [stores, setStores] = useState({})
  useEffect(() => {
    const m = JSON.parse(savedExtras)
    setCustom(m.custom.map((r) => ({ ...r, key: ++rowKey })))
    setStores(m.locations || {})
  }, [savedExtras])
  const extrasChanged = JSON.stringify({ custom: strip(custom), locations: stores }) !== savedExtras
  const setRow = (key, patch) => setCustom((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  // Transactions post entries, so headings are left out: any code ending in
  // 00 (the server refuses those) or an account with accounts under it.
  const postable = useMemo(() => accounts.filter((a) => !isHeading(a, accounts) && !/^\d{2}00$/.test(String(a.account_code))), [accounts])
  const byCode = useMemo(() => Object.fromEntries(accounts.map((a) => [a.account_code, a])), [accounts])
  const others = Object.keys(mapping).filter((k) => !ACCOUNT_ROLES[k]).sort()
  const changed = Object.keys(mapping).filter((k) => (mapping[k] || '') !== (saved[k] || ''))
  // A shared account left unset uses its standard account when the chart has
  // it; a transaction's own account left unset uses the shared one.
  const { data: standard } = useQuery({ queryKey: ['setup-standard'], queryFn: getSetupStandard })
  const stdCode = useMemo(() => Object.fromEntries((standard?.defaults || []).map((d) => [d.name, d.code])), [standard])
  const fallback = (k) => (postable.some((a) => a.account_code === stdCode[k]) ? stdCode[k] : '')
  const effective = (k) => mapping[k] || (ACCOUNT_ROLES[k]?.inherits ? effective(ACCOUNT_ROLES[k].inherits) : fallback(k))
  // Counted over the rows on the page: one with no account at all, and a
  // shared one left on its standard account.
  const pageKeys = useMemo(() => [...new Set(TRANSACTIONS.flatMap((t) => t.rows.map(([k]) => k)))].filter((k) => k !== 'ExpenseAccount'), [])
  const missing = pageKeys.filter((k) => !effective(k)).length
  const unset = pageKeys.filter((k) => !ACCOUNT_ROLES[k].inherits && !mapping[k]).length

  const status = (key) => {
    const role = ACCOUNT_ROLES[key] || {}
    const code = mapping[key]
    if (!code) {
      if (role.inherits) return effective(key) ? null : <StatusBadge label="Not set" tone="warn" />
      if (key === 'ExpenseAccount' || fallback(key)) return null
      return <StatusBadge label="Not set" tone="warn" />
    }
    const a = byCode[code]
    if (!a) return <StatusBadge label="Not in the chart" tone="bad" />
    if (role.type && a.type !== role.type) return <StatusBadge label={`Not ${role.type.toLowerCase()}`} tone="warn" />
    return null
  }

  // What a blank choice means: the standard account (a transaction's own
  // account uses the shared one); an expense's account is left to the form.
  const blankText = (key) => {
    if (key === 'ExpenseAccount') return 'Let the expense form choose'
    const role = ACCOUNT_ROLES[key] || {}
    const code = role.inherits ? effective(role.inherits) : fallback(key)
    return code ? `${code} · ${byCode[code]?.account_name || ''} (standard)` : 'Not set'
  }

  const choices = (type, current) => {
    const list = postable.filter((a) => fits(a, type))
    const cur = byCode[current]
    return cur && !list.includes(cur) ? [cur, ...list] : list
  }

  const save = async () => {
    const blank = custom.find((r) => !r.name.trim() || !r.account_code)
    if (blank) { setError(blank.name.trim() ? `Choose an account for “${blank.name.trim()}”.` : 'Give each extra account a name.'); return }
    setSaving(true)
    setError('')
    try {
      if (changed.length) await saveControlAccounts(Object.fromEntries(changed.map((k) => [k, mapping[k] || ''])))
      if (extrasChanged) await saveDefaultExtras({ custom: strip(custom), locations: stores })
      await Promise.all(['control-accounts', 'default-extras', 'default-choices'].map((k) => qc.invalidateQueries({ queryKey: [k] })))
      notify('Transaction accounts saved')
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Couldn’t save the transaction accounts.')
    } finally {
      setSaving(false)
    }
  }

  // A plain function (not a component) so the select keeps focus as it changes.
  const select = (k, type, label) => (
    <select className="ci-select mf-default-select" value={mapping[k] || ''} onChange={(e) => setMapping((m) => ({ ...m, [k]: e.target.value }))} aria-label={label || k}>
      <option value="">{blankText(k)}</option>
      {choices(type, mapping[k]).map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.account_name}</option>)}
    </select>
  )

  // "Shared": the other transactions that use the same account.
  const shared = (key, title) => {
    const also = (USED_BY[key] || []).filter((t) => t !== title)
    return also.length > 0 && (
      <span className="mf-shared">Shared<HelpTip text={`The same account is used for ${also.join(', ')}. Changing it here changes it there too, so the amounts owed and the stock stay in step.`} /></span>
    )
  }

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Transaction accounts <HelpTip text="When you make a sale, receive stock or pay a bill, the app writes the journal entry for you. Here you choose which accounts each kind of transaction uses. Choose accounts under a heading, not the heading itself." /></h1>
            <div className="ci-title-meta">Which accounts each kind of transaction posts to.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-ghost" to="/master/accounts">Chart of accounts</Link>
          <button type="button" className="ci-btn ci-btn-primary" disabled={(!changed.length && !extrasChanged) || saving} onClick={save}><Save size={15} />{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      <DefaultsGuide />
      {error && <ErrorBanner message={error} />}
      {!isLoading && missing > 0 && (
        <p className="mf-notice"><StatusBadge label={`${missing} not set`} tone="warn" /> Your chart has no standard account for these, so the transactions that need them stop until they’re set. <Link to="/master/setup">Account setup</Link> adds the standard ones.</p>
      )}
      {!isLoading && missing === 0 && unset > 0 && (
        <p className="mf-notice"><StatusBadge label={`${unset} using the standard account`} tone="muted" /> Accounts left unset use the standard account shown beside them. Choose another to change it.</p>
      )}

      {isLoading || loadingAccounts ? <Spinner label="Loading transaction accounts…" /> : (
        <>
          {TRANSACTIONS.map((t) => (
            <section key={t.id} className="ci-table-wrap mf-default-group">
              <h2 className="mf-default-title">{t.title}</h2>
              <p className="mf-default-intro">{t.intro}</p>
              <ul className="mf-default-list">
                {t.rows.map(([key, label]) => (
                  <li key={key} className={changed.includes(key) ? 'changed' : ''}>
                    <span className="mf-default-label">{label}<HelpTip text={ACCOUNT_ROLES[key].help} />{shared(key, t.title)}</span>
                    {select(key, ACCOUNT_ROLES[key].type, `${t.title}: ${label}`)}
                    <span className="mf-default-status">{status(key)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <section className="ci-table-wrap mf-default-group">
            <h2 className="mf-default-title">Extra accounts to choose from <HelpTip text="More cash, bank, receivable or payable accounts, such as a second bank account or Staff advances. They’re offered beside the usual account when you record a payment, an invoice, a bill or an expense." /></h2>
            {custom.length > 0 && (
              <ul className="mf-default-list mf-own-list">
                {custom.map((r) => {
                  const kind = KIND[r.kind] || KINDS[0]
                  const pick = postable.filter((a) => a.type === kind.type)
                  return (
                    <li key={r.key}>
                      <input className="mf-own-name" value={r.name} maxLength={80} placeholder="Name, e.g. Staff advances" aria-label="Name" onChange={(e) => setRow(r.key, { name: e.target.value })} />
                      <select className="ci-select mf-own-kind" value={r.kind} aria-label={`${r.name || 'New account'}: works as`}
                        onChange={(e) => setRow(r.key, { kind: e.target.value, account_code: KIND[e.target.value].type === kind.type ? r.account_code : '' })}>
                        {KINDS.map((k) => <option key={k.id} value={k.id}>Works as {k.label.toLowerCase()}</option>)}
                      </select>
                      <select className="ci-select mf-default-select" value={r.account_code} aria-label={`${r.name || 'New account'}: account`} onChange={(e) => setRow(r.key, { account_code: e.target.value })}>
                        <option value="">Choose an account…</option>
                        {r.account_code && !pick.some((a) => a.account_code === r.account_code) && <option value={r.account_code}>{r.account_code} · {byCode[r.account_code]?.account_name || 'not in the chart'}</option>}
                        {pick.map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.account_name}</option>)}
                      </select>
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm mf-own-remove" aria-label={`Remove ${r.name || 'this account'}`}
                        onClick={() => {
                          setCustom((rows) => rows.filter((x) => x.key !== r.key))
                          // A store using it goes back to the POS account.
                          setStores((st) => Object.fromEntries(Object.entries(st).map(([id, roles]) => [id, Object.fromEntries(Object.entries(roles || {}).filter(([, c]) => c !== r.account_code))])))
                        }}><X size={14} /></button>
                    </li>
                  )
                })}
              </ul>
            )}
            <div className="mf-own-foot">
              <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setCustom((rows) => [...rows, { key: ++rowKey, default_id: null, name: '', kind: 'Card', account_code: '' }])}><Plus size={14} />Add an account</button>
              {custom.length === 0 && <span className="mf-default-note-inline">For example a second bank account, or Staff advances as a receivable.</span>}
            </div>
          </section>
          {(extras?.location_list || []).length > 0 && (
            <section className="ci-table-wrap mf-default-group">
              <h2 className="mf-default-title">POS at each store <HelpTip text="A store can use its own cash drawer or bank account for its sales and refunds at the POS. Leave it on the POS account to use the one under POS sales." /></h2>
              <ul className="mf-default-list mf-store-list">
                {extras.location_list.map((l) => (
                  <li key={l.location_id}>
                    <span className="mf-default-label">{l.name}</span>
                    {[['Cash', 'TillCash'], ['Card', 'TillCard']].map(([role, till]) => {
                      const pick = stores[l.location_id]?.[role] || ''
                      const usual = effective(till)
                      const allowedCodes = [mapping[role], mapping[till], ...custom.filter((c) => c.kind === role && c.account_code).map((c) => c.account_code)].filter(Boolean)
                      return (
                        <label key={role} className="mf-store-pick">
                          <span>{role === 'Cash' ? 'Cash' : 'Card payments'}</span>
                          <select className="ci-select" value={pick} aria-label={`${l.name} ${role}`}
                            onChange={(e) => setStores((st) => ({ ...st, [l.location_id]: { ...(st[l.location_id] || {}), [role]: e.target.value } }))}>
                            <option value="">The POS account{usual ? ` (${usual})` : ''}</option>
                            {[...new Set([...allowedCodes, pick].filter(Boolean))].filter((c) => c !== usual || pick === c)
                              .map((c) => <option key={c} value={c}>{c} · {byCode[c]?.account_name || 'not in the chart'}</option>)}
                          </select>
                        </label>
                      )
                    })}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="ci-table-wrap mf-default-group">
            <h2 className="mf-default-title">Your own transactions <HelpTip text="For a kind of transaction the app doesn’t post by itself, such as gift cards sold or tips paid out, set up a transaction type: choose its debit and credit accounts once, then record it with just an amount." /></h2>
            <p className="mf-default-note">Something the app doesn’t post for you? <Link to="/accounting/transaction-types">Set up a transaction type</Link> with its own accounts, then record it with just an amount and a date.</p>
          </section>
          {others.length > 0 && (
            <section className="ci-table-wrap mf-default-group">
              <h2 className="mf-default-title">Other <HelpTip text="Extra accounts added for your company by imports or custom set-ups." /></h2>
              <ul className="mf-default-list">
                {others.map((k) => (
                  <li key={k} className={changed.includes(k) ? 'changed' : ''}>
                    <span className="mf-default-label">{k}</span>
                    {select(k)}
                    <span className="mf-default-status">{status(k)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  )
}
